import React, { useContext, useEffect, useState } from 'react';
import { IPaginationParams } from '../../../services/types';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import { Add } from '@mui/icons-material';
import {
  ISportUnityFilterParams,
  ISportUnityProps,
} from '../../../services/Sports/Unities/types';
import {
  useGetAllSportUnitiesByLicenseAsync,
  useGetAllSportUnitiesByLicenseCountAsync,
} from '../../../services/Sports/Unities/hooks/useQuery';
import {
  useDeleteSportUnity,
  useGetSportUnity,
} from '../../../services/Sports/Unities/hooks/useMutation';
import SportUnityEditModal from './_components/SportUnityEditModal';
import { AppContext } from '../../../context/AppContextProvider';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { t } from 'i18next';

const SportUnitiesPage = () => {
  const [filter, setFilter] = useState<ISportUnityFilterParams>({
    search: '',
  });
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isVisibleEditModal, setIsVisibleEditModal] = useState<boolean>(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [selectedUnity, setSelectedUnity] = useState<
    ISportUnityProps | undefined
  >(undefined);
  const {
    data: unities,
    refetch: refetchAllUnities,
    isLoading: isLoadingUnities,
  } = useGetAllSportUnitiesByLicenseAsync({
    pagination: paginationParams,
    filter,
  });
  const {
    data: totalUnities,
    refetch: refetchUnitiesCount,
    isLoading: isLoadingCount,
  } = useGetAllSportUnitiesByLicenseCountAsync(paginationParams, filter);
  const { mutateAsync: mutateGetUnity } = useGetSportUnity();
  const { mutateAsync: mutateDeleteUnity } = useDeleteSportUnity();

  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const columns: IColumnProps[] = [
    { id: 'name', label: t('sportsPage.unities.name'), width: 'auto' },
    {
      id: 'address.state',
      label: t('sportsPage.unities.state'),
      width: 'auto',
    },
    { id: 'address.city', label: t('sportsPage.unities.city'), width: 'auto' },
    {
      id: 'id',
      label: t('sportsPage.unities.actions'),
      align: 'center',
      width: '0px',
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          isWithDefaultOptionsList
          handleDelete={() => handleDelete(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const refetchAll = async () => {
    setIsShowLoadingComponent(true);
    await refetchAllUnities();
    await refetchUnitiesCount();
  };

  const handleEdit = async (id: number) => {
    await mutateGetUnity(id).then(setSelectedUnity);
    setIsVisibleEditModal(true);
  };

  const handleDelete = async (id: number) => {
    await mutateGetUnity(id).then(setSelectedUnity);
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    if (selectedUnity && selectedUnity.id) {
      setIsShowLoadingComponent(true);
      await mutateDeleteUnity(selectedUnity.id)
        .then(() => {
          setSelectedUnity(undefined);
          setIsVisibleDeleteModal(false);
          showSnackbar('Unidade Esportiva deletada com sucesso!', 'success');
          refetchAll();
        })
        .finally(() => setIsShowLoadingComponent(false));
    }
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteModal(false);
    setSelectedUnity(undefined);
  };

  const handleSaveForm = async () => {
    setSelectedUnity(undefined);
    await refetchAll();
    setIsShowLoadingComponent(false);
  };

  const handleCloseEditionModal = () => {
    setSelectedUnity(undefined);
    setIsVisibleEditModal(false);
  };

  useEffect(() => {
    if (unities && totalUnities) {
      setIsShowLoadingComponent(false);
    }
  }, [unities, totalUnities]);

  return (
    <>
      <TableComponent
        columns={columns}
        rows={unities ?? []}
        sortByName={'name'}
        isLoading={isLoadingUnities || isLoadingCount}
        title={t('sportsPage.sportsFacilities')}
        totalCount={totalUnities}
        searchValue={filter.search}
        onSubmitMainFilter={refetchAll}
        onChangeMainFilter={(search) =>
          setFilter((old) => ({ ...old, search }))
        }
        onChangePageOrRowsPerPage={setPaginationParams}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            {t('sportsPage.newUnity')}
          </ButtonComponent>
        }
      />
      <ConfirmDeleteModalComponent
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja deletar a unidade ${selectedUnity?.name} ?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title={'Deletar Unidade'}
      />
      <SportUnityEditModal
        isOpen={isVisibleEditModal}
        setIsOpen={setIsVisibleEditModal}
        onClose={handleCloseEditionModal}
        selectedUnity={selectedUnity}
        handleSaveForm={handleSaveForm}
      />
    </>
  );
};

export default SportUnitiesPage;
